import React, { Fragment } from 'react';
import merge from 'lodash/merge';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { injectIntl } from 'react-intl';

import { DATA_SOURCE_TYPE, VITALS_ACTIVITY_TYPE } from '../../constants';
import { PERIOD, PROVIDER_OPTION } from '../../../common/Charts/constants';
import InfoWrapper from '../../../common/Charts/InfoWrapper';
import { mapChartData, dataConverter } from '../../../common/Charts/helpers';

import ModalWrapper from '../../../common/Charts/ModalWrapper';
import ChartWrapper from '../../../common/Charts/ChartWrapper';
import ChartProvider from '../../../common/Charts/ChartProvider';
import HistoryWrapper from '../../../common/Charts/HistoryWrapper';
import { DEFAULTS, TIMELINE } from '../../../common/Charts/options';
import { TOOLTIP_VERTICAL_LINE } from '../../../common/Charts/plugins';
import * as bodySurfaceAreaMeasurement from '../../../../redux/profiles/patientHealthboard/body-surface-area-measurements-req';
import * as bodySurfaceAreaMeasurementModal from '../../../../redux/profiles/patientHealthboard/body-surface-area-measurements-modal-req';
import {
  useChartActions,
  useChartFormats,
  useHistoryActions,
  useSafeDataSourceTitle,
} from '../../../common/Charts/hooks';
import {
  selectHistoricMeasurementsList,
  selectHistoricMeasurementsListPending,
  selectBodySurfaceAreaMeasurementEntity,
  selectBodySurfaceAreaMeasurementModalEntity,
  selectBodySurfaceAreaMeasurementEntityPending,
  selectBodySurfaceAreaMeasurementModalEntityPending,
} from '../../../../redux/profiles/patientHealthboard/selectors';
import {
  calculateMinMaxRangeForRawValue,
  getOriginalProviderCode,
  roundPrecision,
} from '../../utils';
import { getCustomOptions } from './options';
import { useCalculatePaddingYAxis } from '../useCalculatePaddingYAxis';

const { DAY } = PERIOD;
const { BODY_SURFACE_AREA } = VITALS_ACTIVITY_TYPE;
const { NAME } = PROVIDER_OPTION;

const BodySurfaceAreaChart = ({ intl: { formatMessage } }) => {
  const historyData = useSelector(selectHistoricMeasurementsList);
  const periodData = useSelector(selectBodySurfaceAreaMeasurementEntity);
  const modalData = useSelector(selectBodySurfaceAreaMeasurementModalEntity);
  const historyLoading = useSelector(selectHistoricMeasurementsListPending);
  const periodLoading = useSelector(
    selectBodySurfaceAreaMeasurementEntityPending,
  );
  const modalLoading = useSelector(
    selectBodySurfaceAreaMeasurementModalEntityPending,
  );
  const { getSafeDataSourceTitle } = useSafeDataSourceTitle();

  const formats = useChartFormats({ formatMessage });

  const { loadHistory } = useHistoryActions({ type: BODY_SURFACE_AREA });

  const { isModal, openDay, closeDay, baseDate, loadPeriod } = useChartActions({
    actions: bodySurfaceAreaMeasurement.actions,
    modalActions: bodySurfaceAreaMeasurementModal.actions,
  });
  const shortMetric = formatMessage({ id: 'patient.healthboard.bsa.metric' });
  const metric = (
    <span>
      {shortMetric}
      <sup>{formatMessage({ id: 'patient.healthboard.bsa.metricIndex' })}</sup>
    </span>
  );

  const paddingYAxisTicks = useCalculatePaddingYAxis();

  const setConfig = (data, date) => ({ period }) => {
    const { min, max } = calculateMinMaxRangeForRawValue(data);
    return {
      type: 'line',
      plugins: [TOOLTIP_VERTICAL_LINE],
      data: mapChartData(
        data,
        getCustomOptions({ period, date, formats, metric, shortMetric }),
      ),
      options: merge(DEFAULTS, TIMELINE[period], {
        scales: {
          yAxes: [
            {
              ticks: {
                min,
                max,
                autoSkip: true,
                autoSkipPadding: paddingYAxisTicks,
                stepSize: 0.1,
                callback(value, _, allData) {
                  const roundedValue = roundPrecision(value, 1);
                  if (allData.includes(roundedValue) && roundedValue !== value)
                    return null;
                  return roundedValue;
                },
              },
            },
          ],
        },
        layout: {
          padding: {
            top: isModal ? 120 : 140,
          },
        },
        tooltips: {
          callbacks: {
            ...DEFAULTS.tooltips,
            footer(points, { datasets, customOptions }) {
              const { providers } = customOptions;
              const { datasetIndex } = points[0];
              const dataset = datasetIndex
                ? datasets[datasetIndex]
                : datasets[0];
              const providerCode = dataset.label.split('.')[0];
              if (providerCode === DATA_SOURCE_TYPE.DEVICES) {
                const devicesPeriodData = (isModal
                  ? modalData
                  : periodData
                ).find(el => el.provider.code === DATA_SOURCE_TYPE.DEVICES);
                const [f] = points;
                const { yLabel } = f;
                const tooltipData = devicesPeriodData.measurements.find(el => {
                  return el.value.baseValue === yLabel;
                });
                if (tooltipData) {
                  return getSafeDataSourceTitle(
                    getOriginalProviderCode(tooltipData.originalProviderCodes),
                    formatMessage,
                  );
                }
              }
              return providers[providerCode][NAME];
            },
          },
        },
      }),
    };
  };

  const context = {
    isModal,
    mark: metric,
    converter: dataConverter.float,
    range: formatMessage({ id: 'patient.healthboard.average' }),
  };

  return (
    <Fragment>
      <ChartProvider extend={context}>
        <ChartWrapper
          onLoad={loadPeriod}
          loading={periodLoading}
          setConfig={setConfig(periodData)}
        />

        <InfoWrapper
          subtitle={formatMessage({ id: 'patient.healthboard.averageData' })}
          title={formatMessage({
            id: 'patient.healthboard.titleHistoric.bsa',
          })}
        >
          <HistoryWrapper
            unit={DAY}
            onClick={openDay}
            data={historyData}
            onLoad={loadHistory}
            loading={historyLoading}
          />
        </InfoWrapper>
      </ChartProvider>

      <ModalWrapper
        title={formatMessage({
          id: 'patient.healthboard.bsa',
        })}
        open={isModal}
        onClose={closeDay}
      >
        <ChartProvider extend={context}>
          <ChartWrapper
            isModal
            loading={modalLoading}
            setConfig={setConfig(modalData, baseDate)}
            footer={<HistoryWrapper isModal data={modalData} />}
          />
        </ChartProvider>
      </ModalWrapper>
    </Fragment>
  );
};

BodySurfaceAreaChart.propTypes = {
  intl: PropTypes.object.isRequired,
};

export default injectIntl(BodySurfaceAreaChart);



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/TimeScalableChart/BodySurfaceAreaChart/index.jsx